import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Panel } from "@/components/panel";
import { StatCard } from "@/components/stat-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiGet } from "@/lib/api-client";
import { deriveSearchResults } from "@/lib/derive-search-results";
import { asCurrency } from "@/lib/format";
import { pickStableKey } from "@/lib/stable-key";
import { asRecord, asArray } from "@/lib/type-utils";
import { useAgentsOverview } from "@/sdk/hooks/use-agents";
import { interpretWalletBalance, useTradingBalance } from "@/sdk/hooks/use-trading";
import { EventStreamPanel } from "@/features/terminal/panels/EventStreamPanel";

export function DashboardPage() {
  const [searchQuery, setSearchQuery] = useState("");

  // Agent system status. A failed read leaves `agents` empty — that must
  // render as "unavailable", never as a real-looking "0/0 running".
  const {
    data: agentOverview,
    isError: agentsError,
  } = useAgentsOverview(10_000);
  const agents = agentOverview?.agents ? Object.values(agentOverview.agents) : [];
  const runningAgents = agents.filter((a) => a.state === "running" || a.running === true);
  const totalPositions = agents.reduce(
    (s, a) => s + (a.open_positions ?? a.n_open_positions ?? 0),
    0,
  );

  // Wallet balance — use the shared hook (single query key) so the dashboard
  // and the terminal panel never issue duplicate requests or drift out of sync.
  // A failed read (geoblocked cloud IP / creds not injected) returns usdc:0 with
  // error/_dev_fallback/trading_enabled:false — that is NOT a real zero, and a
  // still-loading read is NOT a failure. interpretWalletBalance keeps the three
  // states distinct so the card shows a value, "…", or "Unavailable" correctly.
  const tradingBalanceQuery = useTradingBalance(30_000);
  const wallet = interpretWalletBalance(tradingBalanceQuery.data, {
    isError: tradingBalanceQuery.isError,
    isLoading: tradingBalanceQuery.isPending,
  });

  // Signals
  const contrarianQuery = useQuery({
    queryKey: ["signals-contrarian"],
    queryFn: () => apiGet<unknown>("/api/signals/contrarian?limit=20"),
    refetchInterval: 30_000,
  });
  const contrarian = asRecord(contrarianQuery.data);
  const opportunities = asArray(contrarian.opportunities);

  // Pipeline health
  const summaryQuery = useQuery({
    queryKey: ["signals-summary"],
    queryFn: () => apiGet<unknown>("/api/signals/summary"),
    refetchInterval: 30_000,
  });
  const summary = asRecord(summaryQuery.data);
  const pipeline = asRecord(summary.pipeline);
  const activeMarkets = typeof pipeline.active_markets === "number" ? pipeline.active_markets : 0;

  // Market search
  const searchMutation = useMutation({
    mutationFn: (query: string) =>
      apiGet<unknown>(
        `/api/markets/explore?search=${encodeURIComponent(query)}&limit=10&sort_by=liquidity&sort_order=desc`,
      ),
  });
  // Bug #18 (audit 2026-05-06): the prior implementation kept ``searchResults``
  // in a ``useState`` slot and copied data over in ``onSuccess``, which
  // duplicated the mutation's source of truth and could lag behind a refire.
  // ``searchMutation.data`` is now the only source; ``useMemo`` derives the
  // rendered list directly.
  const searchResults = useMemo(
    () => deriveSearchResults(searchMutation.data),
    [searchMutation.data],
  );

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Status Cards */}
      <Panel title="Overview">
        <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
          <StatCard
            label="Agents"
            value={agentsError ? "—" : `${runningAgents.length}/${agents.length} running`}
            tone={agentsError ? "warning" : runningAgents.length > 0 ? "success" : "warning"}
            hint={agentsError ? "Backend unreachable" : `${totalPositions} open positions`}
          />
          <StatCard
            label={wallet.status === "available" ? `${wallet.label ?? "Wallet"} Balance` : "Wallet Balance"}
            value={
              wallet.status === "available"
                ? asCurrency(wallet.usdc)
                : wallet.status === "loading"
                  ? "…"
                  : "Unavailable"
            }
            tone={wallet.status === "unavailable" ? "warning" : undefined}
            hint={
              wallet.status === "available"
                ? wallet.hint ?? "Polymarket USDC"
                : wallet.status === "loading"
                  ? "Checking…"
                  : wallet.reason === "not-configured"
                    ? "Trading not configured"
                    : "Can't reach Polymarket"
            }
          />
          <StatCard
            label="Active Markets"
            value={summaryQuery.isError ? "—" : String(activeMarkets)}
            tone={summaryQuery.isError ? "warning" : undefined}
            hint={summaryQuery.isError ? "Backend unreachable" : "Monitored by signal engine"}
          />
          <StatCard
            label="Signals Found"
            value={contrarianQuery.isError ? "—" : String(opportunities.length)}
            tone={contrarianQuery.isError ? "warning" : undefined}
            hint={contrarianQuery.isError ? "Backend unreachable" : "Contrarian opportunities"}
          />
        </div>
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        {/* Market Search */}
        <Panel
          title="Market Search"
          description="Search active Polymarket markets by keyword."
        >
          <form
            className="flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (searchQuery.trim().length > 0) {
                searchMutation.mutate(searchQuery.trim());
              }
            }}
          >
            <Input
              className="flex-1"
              placeholder="Search markets (e.g. bitcoin, election, fed)"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            <Button type="submit" disabled={searchMutation.isPending}>
              {searchMutation.isPending ? "Searching..." : "Search"}
            </Button>
          </form>

          <div className="mt-4 space-y-1.5">
            {searchResults.length === 0 && !searchMutation.isPending && (
              <p className="py-4 text-center text-sm text-muted">
                Search for a market to see results
              </p>
            )}
            {searchResults.slice(0, 10).map((market, index) => {
              const question =
                (typeof market.question === "string" && market.question) ||
                (typeof market.title === "string" && market.title) ||
                `Market ${index + 1}`;
              const yesPrice =
                typeof market.yes_price === "number"
                  ? market.yes_price
                  : typeof market.price === "number"
                    ? market.price
                    : null;
              const volume =
                typeof market.volume_total === "number"
                  ? market.volume_total
                  : typeof market.volume === "number"
                    ? market.volume
                    : null;

              return (
                <div
                  key={pickStableKey(market, ["market_id", "condition_id", "id"])}
                  className="flex items-center justify-between rounded-lg border border-border/60 bg-panel/50 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-text">{question}</div>
                    {volume !== null && (
                      <div className="mt-0.5 text-xs text-muted">
                        Vol: {asCurrency(volume)}
                      </div>
                    )}
                  </div>
                  {yesPrice !== null && (
                    <div className="ml-3 text-right">
                      <div className="text-sm font-mono font-bold text-emerald-600 dark:text-emerald-400">
                        {(yesPrice * 100).toFixed(1)}%
                      </div>
                      <div className="text-[10px] text-muted">YES</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>

        {/* Signal Feed */}
        <Panel
          title="Signal Feed"
          description="Top contrarian signals from the signal engine."
        >
          <div className="space-y-1.5">
            {opportunities.length === 0 && (
              <p className="py-4 text-center text-sm text-muted">
                No signals detected right now
              </p>
            )}
            {opportunities.slice(0, 10).map((opp, index) => {
              const o = opp as Record<string, unknown>;
              const signal = String(o.signal ?? "NEUTRAL");
              const price =
                typeof o.current_price === "number" ? o.current_price : null;
              const confidence =
                typeof o.confidence === "number" ? o.confidence : null;
              const question =
                typeof o.question === "string" && o.question
                  ? o.question
                  : typeof o.market_id === "string"
                    ? String(o.market_id).substring(0, 12) + "..."
                    : `Signal ${index + 1}`;

              const isBuy = signal.includes("BUY");
              const isSell = signal.includes("SELL");

              return (
                <div
                  key={pickStableKey(o, ["market_id", "condition_id", "id"])}
                  className="flex items-center justify-between rounded-lg border border-border/60 bg-panel/50 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-text">{question}</div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs">
                      <span
                        className={`font-bold ${isBuy ? "text-emerald-600 dark:text-emerald-400" : isSell ? "text-red-600 dark:text-red-400" : "text-muted"}`}
                      >
                        {signal}
                      </span>
                      {confidence !== null && (
                        <span className="text-muted">
                          {(confidence * 100).toFixed(0)}% conf
                        </span>
                      )}
                    </div>
                  </div>
                  {price !== null && (
                    <div className="ml-3 text-right font-mono text-sm text-text/80">
                      {(price * 100).toFixed(1)}%
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>
      </div>

      {/* Live Event Stream */}
      <Panel title="Live Activity" description="Real-time agent signals and trades.">
        <div className="h-64 overflow-hidden rounded-lg border border-border/60">
          <EventStreamPanel />
        </div>
      </Panel>

      {/* Agent Cards */}
      {agents.length > 0 && (
        <Panel title="Agent Status">
          <div className="grid gap-2 md:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(14rem,1fr))]">
            {agents.map((agent) => {
              const name = agent.name ?? agent.agent_name ?? agent.agent_id;
              const isRunning = agent.state === "running" || agent.running === true;
              const positions = agent.open_positions ?? agent.n_open_positions ?? 0;
              const pnl = agent.realized_pnl ?? 0;
              const trades = agent.total_trades ?? 0;
              const paper = agent.paper_trading;

              return (
                <div
                  key={agent.agent_id}
                  className="rounded-lg border border-border/60 bg-panel/50 p-3"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-2 w-2 rounded-full ${isRunning ? "bg-emerald-500" : "bg-muted/50"}`}
                    />
                    <span className="text-sm font-medium text-text">{name}</span>
                    {paper && (
                      <span className="ml-auto rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700 dark:bg-amber-950 dark:text-amber-400">
                        Paper
                      </span>
                    )}
                    {!paper && (
                      <span className="ml-auto rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
                        Live
                      </span>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <div className="text-muted">Positions</div>
                      <div className="font-mono text-text/80">{positions}</div>
                    </div>
                    <div>
                      <div className="text-muted">Trades</div>
                      <div className="font-mono text-text/80">{trades}</div>
                    </div>
                    <div>
                      <div className="text-muted">P&L</div>
                      <div
                        className={`font-mono ${pnl >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}
                      >
                        ${pnl.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      )}
    </div>
  );
}
